/* eslint-disable max-len */

import * as React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ChevronDown, X } from 'lucide-react';

export interface MultiSelectOption {
  /** Unique string ID */
  value: string;
  /** Visible label in the list & chip */
  label: string;
  /** Optional avatar URL (used only when `showAvatar`) */
  avatarUrl?: string;
}

interface MultiSelectProps {
  /** Button label when nothing is selected */
  placeholder: string;
  /** Any icon element to show on the left side of the button */
  icon?: React.ReactNode;

  /** Full flat list of options */
  options: MultiSelectOption[];

  /** Controlled selected values */
  value: string[];
  /** Callback whenever the array changes */
  onChange: (v: string[]) => void;

  /** Show search field inside the pop-over (default = true) */
  searchable?: boolean;
  /** Show avatar column (default = true if any option has `avatarUrl`) */
  showAvatar?: boolean;

  /** Optional extra Tailwind classes for the trigger button */
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function MultiSelect({
  placeholder,
  icon,
  options,
  value,
  onChange,
  searchable = false,
  showAvatar,
  className,
}: MultiSelectProps) {
  const autoShowAvatar =
    showAvatar ?? options.some(o => o.avatarUrl !== undefined);

  /** Toggle one value */
  const toggle = (val: string) =>
    onChange(
      value.includes(val) ? value.filter(v => v !== val) : [...value, val],
    );

  return (
    <Popover>
      {/* ───── Trigger button ───── */}
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 max-w-xs text-muted ${className ?? ''}`}
        >
          {icon}
          {value.length ? (
            <>
              {/* selected chips (truncate when too many) */}
              <div className="flex gap-1">
                {value.slice(0, 1).map(v => {
                  const opt = options.find(o => o.value === v)!;
                  return (
                    <Badge
                      key={v}
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 cursor-pointer text-muted bg-[#F4F4F5]"
                      // onClick={(e) => {
                      //   e.stopPropagation();
                      //   toggle(v);
                      // }}
                    >
                      {opt.label}
                      {/* <X className="size-3 ml-1" /> */}
                    </Badge>
                  );
                })}

                {/* show “+N” if more than 3 selected */}
                {value.length > 1 && (
                  <Badge variant="normal" className="px-2 text-muted">
                    +{value.length - 1}
                  </Badge>
                )}
              </div>
              <X
                className="size-4 shrink-0"
                onClick={e => {
                  e.stopPropagation();
                  onChange([]);
                }}
              />
            </>
          ) : (
            placeholder
          )}

          {value.length === 0 && (<ChevronDown size={16} color="#71717A" />)}
          
        </Button>
      </PopoverTrigger>

      {/* ───── Popover panel ───── */}
      <PopoverContent className="w-72 p-0">
        <Command loop>
          {searchable && (
            <CommandInput
              placeholder="Search here"
              className="h-9 px-2 py-1 my-1 ring-0 outline-none border-0 focus:outline-none focus:ring-0 focus:border-current"
            />
          )}

          <CommandList className="max-h-60 p-1 [&::-webkit-scrollbar-thumb]:bg-[#09090B]">
            <CommandEmpty>No result.</CommandEmpty>

            {options.map(opt => {
              const checked = value.includes(opt.value);

              return (
                <CommandItem
                  key={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                >
                  <Checkbox checked={checked} />

                  {autoShowAvatar && (
                    <Avatar className="h-8 w-8">
                      {opt.avatarUrl ? (
                        <AvatarImage src={opt.avatarUrl} alt={opt.label} />
                      ) : (
                        <AvatarFallback>
                          {opt.label
                            .split(' ')
                            .map(s => s[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  )}

                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
